import React from 'react';
import { Sparkles, Upload, X, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';

export const CargaMasivaDialog = ({
  open,
  onOpenChange,
  cargaMasivaFiles,
  cargaMasivaInputRef,
  resultadosCargaMasiva,
  procesandoCargaMasiva,
  handleCargaMasivaFiles,
  handleCargaMasivaDrop,
  handleRemoveCargaMasivaFile,
  handleProcesarCargaMasiva,
  handleCloseCargaMasiva,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Carga Masiva de Contratos con IA
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
            <p className="font-medium mb-1">¿Cómo funciona?</p>
            <ol className="list-decimal list-inside space-y-1 text-purple-700">
              <li>Subí múltiples archivos de contratos (PDF, DOC, imágenes)</li>
              <li>La IA analiza cada uno y extrae los datos</li>
              <li>Los contratos quedan como <strong>borradores</strong> para tu revisión</li>
              <li>Revisás, editás si es necesario, y aprobás cada uno</li>
            </ol>
          </div>

          {/* Drop zone */}
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
              cargaMasivaFiles.length > 0 ? "border-purple-300 bg-purple-50" : "border-slate-300 hover:border-purple-400"
            )}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleCargaMasivaDrop}
            onClick={() => cargaMasivaInputRef.current?.click()}
          >
            <input
              ref={cargaMasivaInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleCargaMasivaFiles}
            />
            <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
            <p className="text-slate-600 font-medium">
              Arrastrá archivos aquí o hacé clic para seleccionar
            </p>
            <p className="text-xs text-slate-400 mt-1">
              PDF, DOC, DOCX, JPG, PNG
            </p>
          </div>

          {/* Files list */}
          {cargaMasivaFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">
                {cargaMasivaFiles.length} archivo(s) seleccionado(s)
              </p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {cargaMasivaFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2 text-sm">
                    <span className="truncate flex-1">{file.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleRemoveCargaMasivaFile(idx); }}
                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          {resultadosCargaMasiva && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="font-medium text-green-800 mb-2">
                ✓ Procesamiento completado
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>{resultadosCargaMasiva.pendientes} pendientes de revisión</span>
                </div>
                {resultadosCargaMasiva.errores > 0 && (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-600" />
                    <span>{resultadosCargaMasiva.errores} con errores</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCloseCargaMasiva}>
            Cerrar
          </Button>
          <Button
            onClick={handleProcesarCargaMasiva}
            disabled={procesandoCargaMasiva || cargaMasivaFiles.length === 0}
          >
            {procesandoCargaMasiva ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Procesar con IA
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
